import { FC, useEffect, useState } from "react";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";
import type { PdfOutlineItem } from "../../../core/pdf/pdf-scene";

interface PdfOutlineMenuProps {
  outlinePanelReveal: boolean;
  pageCount: number;
  pageNumber: number;
  sheetNumbers: { [pageNumber: number]: string };
  sheetsRead: boolean;
}

interface PdfOutlineRowProps {
  item: PdfOutlineItem;
  depth: number;
}

// Bookmarks nest; a row with children opens and closes them, and its title
// still jumps to where it points.
const PdfOutlineRow: FC<PdfOutlineRowProps> = ({ item, depth }) => {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = item.items && item.items.length > 0;

  return (
    <>
      <div
        className="pdfOutlineRow"
        style={{ paddingLeft: `${10 + depth * 14}px` }}
        title={item.title}
        onClick={() => {
          PdfHandler.GoToOutlineItem(item);
        }}
      >
        {hasChildren ? (
          <span
            className="pdfOutlineToggle"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? "▾" : "▸"}
          </span>
        ) : (
          <span className="pdfOutlineToggle" />
        )}
        <span className="pdfOutlineTitle">{item.title}</span>
      </div>
      {hasChildren && expanded ? (
        item.items.map((child, index) => (
          <PdfOutlineRow key={index} item={child} depth={depth + 1} />
        ))
      ) : (
        <></>
      )}
    </>
  );
};

export const PdfOutlineMenu: FC<PdfOutlineMenuProps> = ({
  outlinePanelReveal,
  pageCount,
  pageNumber,
  sheetNumbers,
  sheetsRead,
}) => {
  const [outline, setOutline] = useState<PdfOutlineItem[] | null>(null);

  // read again once a document has loaded, which is when pageCount moves
  useEffect(() => {
    if (!outlinePanelReveal || pageCount === 0) return;

    let abandoned = false;
    PdfHandler.GetOutline().then((outlineItems) => {
      if (!abandoned) {
        setOutline(outlineItems);
      }
    });

    return () => {
      abandoned = true;
    };
  }, [outlinePanelReveal, pageCount]);

  if (!outlinePanelReveal) return null;

  // the sheets in page order, as read from their title blocks
  const sheets = Object.entries(sheetNumbers).map(
    ([sheetPage, sheetNumber]) => ({
      sheetPage: Number(sheetPage),
      sheetNumber,
    }),
  );
  const hasBookmarks = outline !== null && outline.length > 0;

  return (
    <div className="viewerToolPanel">
      <div className="viewerToolPanelHeader">
        <span className="viewerToolPanelLabel">OUTLINE</span>
        <span className="viewerToolPanelName">Sheets and bookmarks</span>
      </div>
      <div className="viewerToolRowWrapper">
        {sheets.length > 0 ? (
          <>
            <span className="pdfOutlineHeading">Sheets</span>
            {sheets.map(({ sheetPage, sheetNumber }) => (
              <div
                key={sheetPage}
                className={
                  sheetPage === pageNumber
                    ? "pdfOutlineRow pdfOutlineRowSelected"
                    : "pdfOutlineRow"
                }
                onClick={() => {
                  PdfHandler.GoToPage(sheetPage);
                }}
              >
                <span className="pdfOutlineToggle" />
                <span className="pdfOutlineTitle">{sheetNumber}</span>
                <span className="pdfOutlinePage">p. {sheetPage}</span>
              </div>
            ))}
          </>
        ) : !sheetsRead ? (
          <span className="viewerToolRowHint">Reading sheet numbers...</span>
        ) : (
          <></>
        )}
        {hasBookmarks ? (
          <>
            {sheets.length > 0 ? (
              <span className="pdfOutlineHeading">Bookmarks</span>
            ) : (
              <></>
            )}
            {outline.map((item, index) => (
              <PdfOutlineRow key={index} item={item} depth={0} />
            ))}
          </>
        ) : outline !== null && sheetsRead && sheets.length === 0 ? (
          <span className="viewerToolRowHint">
            This PDF has no bookmarks, and no sheet numbers were found in its
            title blocks. Use Pages to move through it.
          </span>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default PdfOutlineMenu;
